import { makeAutoObservable, runInAction } from 'mobx';
import { api } from '../../shared/api/api';
import { message, notification } from 'antd';
import { storage } from '../../shared/lib/storage';
import { rootStore } from '../../shared/stores/root-store';

// Активная площадка из селектора (как в housekeeping-сторах). WS-консьюмер
// иначе берёт current_platform из JWT, и суперадмин, работающий «через
// селектор», не может отвечать в чате выбранной площадки.
function activePlatformId(): number | null {
  const ps = (rootStore as any).platforms;
  return ps?.activePlatformId ?? ps?.activePlatform?.id ?? null;
}

/* ────────────────────────── Types ────────────────────────── */

export interface ChatMessage {
  id: string | number;
  chat_id: number;
  message: string;
  text?: string;
  creation_date: string;
  sender_type: 0 | 1; // 0 = GUEST, 1 = PLATFORM (staff)
  ack_date: string | null;
}

export type ChatChannel = 'app' | 'telegram' | 'whatsapp';

export interface Chat {
  id: number;
  guest_name: string;
  /** Имя известно, или `guest_name` — заглушка «Аноним». */
  has_guest_name?: boolean;
  guest_auth_type: string;
  guest_token?: string;
  guest_room: string;
  channel: ChatChannel;
  unread_messages: number;
  creation_date: string;
  update_date: string;
  last_message: {
    message: string;
    creation_date: string;
  } | null;
}

export interface GuestInfo {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  email: string;
  birthday: string | null;
  total_nights: number;
  total_orders: number;
  total_spent: number;
  visits_count: number;
  first_visit: string | null;
  last_visit: string | null;
  notes: string;
  tags: string[];
  loyalty_balance: number | null;
  loyalty_level: string | null;
  loyalty_discount: number | null;
  has_loyalty: boolean;
}

export interface GuestOrder {
  id: number;
  number: string;
  creation_date: string;
  status: number;
  items: { name: string; item_count: number; total_sum: number }[];
  payment: { price: number; payment_type: number; is_paid: boolean } | null;
}

export interface LoyaltyTransaction {
  id: number;
  transaction_type: 'earn' | 'redeem' | 'adjust' | 'expire';
  points: number;
  balance_after: number;
  description: string;
  created_at: string;
}

/* WebSocket message types */
const WS_MSG = {
  CREATE: 'MsgCreateChatMessage',
  INCOMING: 'MsgIncomingChatMessage',
  DATA_CHANGED: 'MsgChatDataChanged',
  UNREAD_CHANGED: 'MsgUnreadChatsChangedMessage',
  OK: 'MsgOk',
  ERROR: 'MsgError',
  NEW_CHAT: 'MsgNewChatCreated',
} as const;

const COMMENT_DELIMITER = '+$#';

/* ────────────────────────── Store ────────────────────────── */

export class ChatStore {
  chats: Chat[] = [];
  totalChats = 0;
  isLoadingChats = false;
  isLoadingMoreChats = false;
  showAll = true; // true = все, false = свои
  channelFilter: ChatChannel | 'all' = 'all';

  activeChat: Chat | null = null;
  messages: ChatMessage[] = [];
  isLoadingMessages = false;

  comments: string[] = [];
  isLoadingComments = false;

  /** Информация о госте */
  guestInfo: GuestInfo | null = null;
  guestOrders: GuestOrder[] = [];
  loyaltyTransactions: LoyaltyTransaction[] = [];
  isLoadingGuest = false;

  unreadCount = 0;

  /** Размер страницы при запросе списка чатов */
  limit = 20;

  /** WebSocket */
  private ws: WebSocket | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /* ──────── Computed ──────── */

  get hasMoreChats(): boolean {
    return this.chats.length < this.totalChats;
  }

  get sortedChats(): Chat[] {
    return [...this.chats]
      .filter(chat => chat.last_message !== null) // Скрываем пустые чаты (без сообщений)
      .filter(chat => this.channelFilter === 'all' || (chat.channel || 'app') === this.channelFilter)
      .sort((a, b) => {
        // Unread first, then by update_date desc
        if (a.unread_messages && !b.unread_messages) return -1;
        if (!a.unread_messages && b.unread_messages) return 1;
        return new Date(b.update_date).getTime() - new Date(a.update_date).getTime();
      });
  }

  /* ──────── Filter ──────── */

  setShowAll(all: boolean) {
    this.showAll = all;
    this.fetchChats({ reset: true });
  }

  setChannelFilter(channel: ChatChannel | 'all') {
    this.channelFilter = channel;
  }

  loadMoreChats() {
    return this.fetchChats({ reset: false });
  }

  /* ──────── Select chat ──────── */

  selectChat(chat: Chat) {
    this.activeChat = chat;
    this.messages = [];
    this.comments = [];
    this.guestInfo = null;
    this.guestOrders = [];
    this.loyaltyTransactions = [];
    this.fetchMessages(chat.id);
    this.fetchComments(chat.id);
    this.fetchGuestInfo(chat);
    // Заказы грузим по guest_token чата независимо от того, нашёлся ли
    // профиль гостя по имени — чтобы заказы этого гостя на площадке
    // всегда были видны.
    this.fetchGuestOrders();
  }

  clearActiveChat() {
    this.activeChat = null;
    this.messages = [];
    this.comments = [];
    this.guestInfo = null;
    this.guestOrders = [];
    this.loyaltyTransactions = [];
  }

  /* ──────── REST API ──────── */

  async fetchChats(options?: { reset?: boolean }) {
    const reset = options?.reset !== false;
    if (reset) {
      this.isLoadingChats = true;
    } else {
      if (!this.hasMoreChats || this.isLoadingMoreChats || this.isLoadingChats) return;
      this.isLoadingMoreChats = true;
    }

    const offset = reset ? 0 : this.chats.length;

    try {
      const params: Record<string, unknown> = {
        limit: this.limit,
        offset,
      };
      if (!this.showAll) params.own = 1;

      const res = await api.get('/api/v1/chat/chats', { params });
      const newItems: Chat[] = Array.isArray(res.data.results ?? res.data)
        ? (res.data.results ?? res.data)
        : [];

      runInAction(() => {
        if (reset) {
          this.chats = newItems;
        } else {
          const existingIds = new Set(this.chats.map((c) => c.id));
          for (const c of newItems) {
            if (!existingIds.has(c.id)) {
              this.chats.push(c);
              existingIds.add(c.id);
            }
          }
        }
        if (typeof res.data?.count === 'number') {
          this.totalChats = res.data.count;
        } else {
          this.totalChats = this.chats.length;
        }
      });
    } catch (_e) {
      message.error('Не удалось загрузить чаты');
    } finally {
      runInAction(() => {
        this.isLoadingChats = false;
        this.isLoadingMoreChats = false;
      });
    }
  }

  async fetchUnreadCount() {
    try {
      const res = await api.get('/api/v1/chat/settings');
      runInAction(() => {
        this.unreadCount = res.data?.new_count ?? 0;
      });
    } catch (_e) {
      // silent
    }
  }

  async fetchMessages(chatId: number) {
    this.isLoadingMessages = true;
    try {
      const res = await api.get(`/api/v1/chat/chats/${chatId}/messages`);
      runInAction(() => {
        const data = res.data?.data ?? res.data?.results ?? res.data ?? [];
        this.messages = Array.isArray(data) ? data : [];
      });
    } catch (_e) {
      message.error('Не удалось загрузить сообщения');
    } finally {
      runInAction(() => {
        this.isLoadingMessages = false;
      });
    }
  }

  async markAsRead(chatId: number, messageId: string | number) {
    try {
      await api.post(`/api/v1/chat/chats/${chatId}/messages/${messageId}/ack`);
      runInAction(() => {
        const msg = this.messages.find((m) => m.id === messageId);
        if (msg) msg.ack_date = new Date().toISOString();
        // Уменьшаем счётчик
        const chat = this.chats.find((c) => c.id === chatId);
        if (chat && chat.unread_messages > 0) chat.unread_messages--;
      });
    } catch (_e) {
      // silent
    }
  }

  async fetchComments(chatId: number) {
    this.isLoadingComments = true;
    try {
      const res = await api.get(`/api/v1/chat/chats/${chatId}/comments`);
      runInAction(() => {
        const raw = typeof res.data === 'string' ? res.data : (res.data?.comments ?? '');
        this.comments = raw
          ? raw.split(COMMENT_DELIMITER).filter((c: string) => c.trim())
          : [];
      });
    } catch (_e) {
      runInAction(() => {
        this.comments = [];
      });
    } finally {
      runInAction(() => {
        this.isLoadingComments = false;
      });
    }
  }

  async saveComment(chatId: number, newComment: string) {
    if (!newComment.trim()) return;
    const updated = [...this.comments, newComment.trim()];
    try {
      await api.post(`/api/v1/chat/chats/${chatId}/set-comments`, {
        comments: updated.join(COMMENT_DELIMITER),
      });
      runInAction(() => {
        this.comments = updated;
      });
    } catch (_e) {
      message.error('Не удалось сохранить комментарий');
    }
  }

  /* ──────── Guest info ──────── */

  async fetchGuestInfo(chat: Chat) {
    this.isLoadingGuest = true;
    try {
      // Ищем гостя по имени, а если имени нет — по номеру комнаты.
      //
      // «Есть ли имя» спрашиваем у сервера (`has_guest_name`), а не
      // сравниваем `guest_name` со словом «Аноним». Эта заглушка
      // живёт в трёх местах бэкенда: переименуют одно — сравнение
      // перестанет совпадать, и в поиск уйдёт сама заглушка.
      // Поиск вернёт пусто, карточка гостя останется пустой, и
      // ничего не упадёт — найти такое потом почти невозможно.
      const searchTerm = chat.has_guest_name
        ? chat.guest_name
        : chat.guest_room || '';

      if (!searchTerm) {
        runInAction(() => { this.isLoadingGuest = false; });
        return;
      }

      const res = await api.get('/api/v1/concierge/guests', {
        params: { search: searchTerm, limit: 1 },
      });

      const guests = res.data?.results ?? res.data ?? [];
      if (guests.length > 0) {
        const guest = guests[0];
        runInAction(() => {
          this.guestInfo = guest;
        });

        // Заказы грузятся в selectChat по guest_token. Здесь — только
        // транзакции лояльности (если есть программа).
        if (guest.has_loyalty) {
          await this.fetchLoyaltyTransactions(guest.id);
        }
      }
    } catch (_e) {
      // silent — гостя нет в базе
    } finally {
      runInAction(() => { this.isLoadingGuest = false; });
    }
  }

  private async fetchGuestOrders() {
    const chatId = this.activeChat?.id;
    if (!chatId) return;
    try {
      // Бэкенд сам резолвит гостя чата (chat → сессия → гость) и отдаёт
      // его заказы на текущей площадке. Раньше фронт пытался угадать
      // client_rhl, но у залогиненного гостя токен чата (crg_*) ≠
      // client_rhl заказа (guest_*), и заказы не находились.
      const pid = activePlatformId();
      const res = await api.get(`/api/v1/chat/chats/${chatId}/orders`, {
        params: pid ? { platform_id: pid } : undefined,
      });
      const orders = res.data?.results ?? res.data ?? [];
      runInAction(() => {
        this.guestOrders = Array.isArray(orders) ? orders.slice(0, 10) : [];
      });
    } catch (_e) {
      // silent
    }
  }

  private async fetchLoyaltyTransactions(guestId: number) {
    try {
      const res = await api.get('/api/v1/concierge/loyalty-transactions', {
        params: { guest: guestId, limit: 10 },
      });
      runInAction(() => {
        this.loyaltyTransactions = res.data?.results ?? res.data ?? [];
      });
    } catch (_e) {
      // silent
    }
  }

  async saveGuestNote(note: string) {
    if (!this.guestInfo) return;
    try {
      await api.patch(`/api/v1/concierge/guests/${this.guestInfo.id}`, { notes: note });
      runInAction(() => {
        if (this.guestInfo) this.guestInfo.notes = note;
      });
    } catch (_e) {
      message.error('Не удалось сохранить заметку');
    }
  }

  /* ──────── WebSocket ──────── */

  connectWebSocket() {
    const token = storage.local.get('token');
    if (!token || this.ws) return;

    const apiDomain = window.__API_DOMAIN__ || 'api.dev.resonline.ru';
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const pid = activePlatformId();
    const url = `${protocol}://${apiDomain}/api/v1/chat/ws?a=${token}`
      + (pid ? `&platform_id=${pid}` : '');

    // Запрашиваем разрешение на browser-уведомления
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    try {
      this.ws = new WebSocket(url);

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleWsMessage(data);
        } catch (_e) {
          // ignore parse errors
        }
      };

      this.ws.onclose = () => {
        this.ws = null;
        // Reconnect after 5s
        setTimeout(() => this.connectWebSocket(), 5000);
      };

      this.ws.onerror = () => {
        this.ws?.close();
      };
    } catch (_e) {
      // WebSocket не поддерживается или ошибка
    }
  }

  disconnectWebSocket() {
    if (this.ws) {
      this.ws.onclose = null; // prevent reconnect
      this.ws.close();
      this.ws = null;
    }
  }

  /** Переподключить WS (например, при смене активной площадки в селекторе). */
  reconnectWebSocket() {
    this.disconnectWebSocket();
    this.connectWebSocket();
  }

  private handleWsMessage(data: { type: string; body: any }) {
    switch (data.type) {
      case WS_MSG.INCOMING: {
        const msg: ChatMessage = {
          id: data.body.id ?? Date.now(),
          chat_id: data.body.chat_id,
          message: data.body.message ?? data.body.text ?? '',
          creation_date: data.body.creation_date ?? new Date().toISOString(),
          sender_type: 0,
          ack_date: null,
        };
        let knownChat = false;
        runInAction(() => {
          // Добавляем сообщение если это активный чат
          if (this.activeChat?.id === msg.chat_id) {
            this.messages.push(msg);
          }
          // Обновляем в списке чатов
          const chat = this.chats.find((c) => c.id === msg.chat_id);
          if (chat) {
            knownChat = true;
            chat.unread_messages++;
            chat.last_message = {
              message: msg.message,
              creation_date: msg.creation_date,
            };
            chat.update_date = msg.creation_date;
          }
          this.unreadCount++;
        });

        // Новый чат, которого ещё нет в списке (первое сообщение от
        // гостя) — подтягиваем список, чтобы он появился сразу с
        // бейджем непрочитанных, а не только после ручного обновления.
        if (!knownChat) {
          this.fetchChats();
        }

        // Уведомление в панели (Ant Design notification).
        // duration: 0 — висит, пока админ не закроет его или не откроет
        // чат: тост на 5 секунд легко пропустить, отойдя от ресепшена,
        // и чат оставался без ответа (жалоба Гринвуд 08.07.2026).
        // key = chat_id — новые сообщения того же гостя обновляют одну
        // карточку, а не плодят стопку.
        const chatInList = this.chats.find((c) => c.id === msg.chat_id);
        const guestName = chatInList?.guest_name || data.body.guest_name || 'Гость';
        const msgPreview = msg.message.replace(/<[^>]+>/g, '').slice(0, 80);
        // Если админ прямо сейчас смотрит этот чат — не отвлекаем.
        const chatIsOnScreen =
          this.activeChat?.id === msg.chat_id &&
          typeof document !== 'undefined' &&
          document.visibilityState === 'visible';
        if (!chatIsOnScreen) {
          const notifKey = `guest-chat-${msg.chat_id}`;
          notification.info({
            key: notifKey,
            message: `Сообщение от ${guestName}`,
            description: msgPreview || 'Новое сообщение',
            placement: 'topRight',
            duration: 0,
            onClick: () => {
              notification.destroy(notifKey);
              // Открываем CRM с нужным чатом. Полная навигация надёжнее,
              // чем тянуть router в стор: уведомление может кликнуться
              // с любой страницы ЛК.
              window.location.assign(`/crm?chat=${msg.chat_id}`);
            },
          });
        }

        // Browser Notification (если разрешено)
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            new Notification(`Сообщение от ${guestName}`, {
              body: msgPreview || 'Новое сообщение',
              icon: '/favicon.ico',
            });
          } catch (_e) { /* silent */ }
        }

        break;
      }
      case WS_MSG.OK: {
        // Наше сообщение успешно отправлено
        if (data.body?.chat_id && data.body?.text) {
          runInAction(() => {
            const outgoing: ChatMessage = {
              id: data.body.id ?? `local-${Date.now()}`,
              chat_id: data.body.chat_id,
              message: data.body.text,
              creation_date: new Date().toISOString(),
              sender_type: 1,
              ack_date: null,
            };
            if (this.activeChat?.id === data.body.chat_id) {
              this.messages.push(outgoing);
            }
          });

          // Messenger delivery: отправляем сообщение гостю через Bot API из браузера
          const delivery = data.body.messenger_delivery || data.body.tg_delivery;
          if (delivery) {
            const { type, bot_token, chat_id: msgChatId } = delivery;
            if (bot_token && msgChatId) {
              if (type === 'max') {
                // MAX: POST /messages?chat_id={id}, Authorization header
                fetch(`https://platform-api.max.ru/messages?chat_id=${msgChatId}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': bot_token,
                  },
                  body: JSON.stringify({ text: data.body.text }),
                }).catch((err) => {
                  console.warn('MAX sendMessage failed:', err);
                });
              } else {
                // Telegram: POST bot{token}/sendMessage
                fetch(`https://api.telegram.org/bot${bot_token}/sendMessage`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ chat_id: msgChatId, text: data.body.text }),
                }).catch((err) => {
                  console.warn('Telegram sendMessage failed:', err);
                });
              }
            }
          }
        }
        break;
      }
      case WS_MSG.UNREAD_CHANGED: {
        runInAction(() => {
          this.unreadCount = data.body?.new_count ?? this.unreadCount;
        });
        break;
      }
      case WS_MSG.NEW_CHAT:
      case WS_MSG.DATA_CHANGED: {
        // Перезагрузим список
        this.fetchChats();
        break;
      }
    }
  }

  sendMessage(chatId: number, text: string) {
    const body = text.trim();
    if (!body) return;

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: WS_MSG.CREATE,
        body: { chat_id: chatId, text: body },
      }));
      return;
    }

    // WS не открыт — отправляем через REST, чтобы сообщение реально
    // дошло до гостя (раньше тут была лишь оптимистичная вставка без
    // доставки). Площадку передаём из селектора (мультиплатформа).
    this.sendMessageRest(chatId, body);
  }

  private async sendMessageRest(chatId: number, body: string) {
    const pid = activePlatformId();
    try {
      const res = await api.post(
        `/api/v1/chat/chats/${chatId}/messages`,
        { text: body },
        { params: pid ? { platform_id: pid } : undefined },
      );
      const data = res.data || {};
      runInAction(() => {
        if (this.activeChat?.id === chatId) {
          this.messages.push({
            id: data.id ?? `local-${Date.now()}`,
            chat_id: chatId,
            message: body,
            creation_date: data.creation_date ?? new Date().toISOString(),
            sender_type: 1,
            ack_date: null,
          });
        }
      });
      // Доставка в мессенджеры (TG/MAX) — как в WS OK-хендлере.
      const delivery = data.messenger_delivery;
      if (delivery?.bot_token && delivery?.chat_id) {
        if (delivery.type === 'max') {
          fetch(`https://platform-api.max.ru/messages?chat_id=${delivery.chat_id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: delivery.bot_token },
            body: JSON.stringify({ text: body }),
          }).catch(() => {});
        } else {
          fetch(`https://api.telegram.org/bot${delivery.bot_token}/sendMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ chat_id: delivery.chat_id, text: body }),
          }).catch(() => {});
        }
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось отправить сообщение');
    }
  }
}

/** Глобальный синглтон — используется в Sidebar для badge непрочитанных */
export const globalChatStore = new ChatStore();
